import { ROLE_LABELS } from '@convometry/contracts';
import {
  CountBadge,
  AppShell,
  Avatar,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  NavItem,
  Sidebar,
  Skeleton,
  Topbar,
  Wordmark,
} from '@convometry/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, Bell, Bot, Building2, ChevronDown, Globe2, LayoutDashboard, LogOut, MessagesSquare, Radio, Settings, Smartphone, UserCheck } from 'lucide-react';
import * as React from 'react';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router';
import { activeAlertCount } from '../features/alerts-model.js';
import { useAlertSummary } from '../features/alerts.js';
import { api, ApiError, signOut } from '../lib/api.js';
import { LiveProvider, LiveStore, useLive, useTenantEventStream } from '../lib/live.js';
import { qk } from '../lib/query.js';
import { timeZoneLabel } from '../lib/time.js';
import { makeCan, TenantProvider, useMe, useTenant, type TenantContextValue } from './session.js';

const NAV = [
  { to: 'resumen', label: 'Resumen', icon: <LayoutDashboard aria-hidden /> },
  { to: 'alertas', label: 'Alertas', icon: <Bell aria-hidden /> },
  { to: 'conexiones', label: 'Conexiones', icon: <Smartphone aria-hidden /> },
  { to: 'conversaciones', label: 'Conversaciones', icon: <MessagesSquare aria-hidden /> },
  { to: 'desempeno', label: 'Desempeño', icon: <BarChart3 aria-hidden /> },
  { to: 'asesores', label: 'Asesores', icon: <UserCheck aria-hidden /> },
  { to: 'configuracion', label: 'Configuración', icon: <Settings aria-hidden /> },
] as const;

export function TenantLayout() {
  const { tenantId = '' } = useParams();
  const me = useMe();
  const membership = me.data?.memberships.find((m) => m.tenantId === tenantId);
  const settings = useQuery({
    queryKey: qk.settings(tenantId),
    queryFn: () => api('getSettings', { params: { tenantId } }),
    enabled: !!membership,
  });
  // One live store per tenant: switching businesses never mixes live state or QR codes.
  const storeRef = React.useRef<{ tenantId: string; store: LiveStore } | null>(null);
  if (storeRef.current?.tenantId !== tenantId) storeRef.current = { tenantId, store: new LiveStore() };
  const store = storeRef.current.store;

  if (me.isPending) return <ShellSkeleton />;
  if (me.isError) {
    return (
      <div className="grid min-h-dvh place-items-center bg-surface-canvas p-4">
        <ErrorState description={me.error.message} action={<Button onClick={() => void me.refetch()}>Reintentar</Button>} />
      </div>
    );
  }
  if (!membership) {
    return (
      <div className="grid min-h-dvh place-items-center bg-surface-canvas p-4">
        <EmptyState
          icon={<Building2 aria-hidden />}
          title="No tienes acceso a este negocio"
          description="Pide a un propietario del negocio que te dé acceso. El acceso de soporte de Estateflow también requiere una membresía explícita."
          action={
            <Button asChild variant="secondary">
              <Link to="/">Ir a mis negocios</Link>
            </Button>
          }
        />
      </div>
    );
  }
  if (settings.isPending) return <ShellSkeleton />;
  if (settings.isError) {
    const notFound = settings.error instanceof ApiError && settings.error.kind === 'not_found';
    return (
      <div className="grid min-h-dvh place-items-center bg-surface-canvas p-4">
        <ErrorState
          title={notFound ? 'Negocio no disponible' : undefined}
          description={settings.error.message}
          action={<Button onClick={() => void settings.refetch()}>Reintentar</Button>}
        />
      </div>
    );
  }

  const ctx: TenantContextValue = {
    tenantId,
    membership,
    me: me.data,
    settings: settings.data,
    timeZone: settings.data.timezone,
    role: membership.role,
    can: makeCan(membership.role),
  };

  return (
    <TenantProvider value={ctx}>
      <LiveProvider value={store}>
        <LiveStream tenantId={tenantId} store={store} />
        <AppShell navLabel="Principal" sidebar={<TenantSidebar />} topbar={<TenantTopbar />}>
          <Outlet />
        </AppShell>
      </LiveProvider>
    </TenantProvider>
  );
}

function LiveStream({ tenantId, store }: { tenantId: string; store: LiveStore }) {
  useTenantEventStream(tenantId, store);
  return null;
}

function TenantSidebar() {
  const { tenantId, membership } = useTenant();
  const alertSummary = useAlertSummary();
  const activeAlerts = activeAlertCount(alertSummary.data);
  return (
    <Sidebar
      brand={
        <Link to={`/t/${tenantId}/resumen`} aria-label="Convometry — ir al resumen" className="rounded-control focus-visible:outline-2 focus-visible:outline-focus">
          <Wordmark endorsement />
        </Link>
      }
      footer={
        <p className="px-2 text-helper text-text-secondary">
          Acceso: <span className="font-medium text-text-primary">{ROLE_LABELS[membership.role]}</span>
        </p>
      }
    >
      {NAV.map((item) => (
        <NavItem
          key={item.to}
          label={item.label}
          icon={item.icon}
          badge={item.to === 'alertas' ? <CountBadge count={activeAlerts} label="alertas activas" /> : undefined}
          render={({ className, children }) => (
            <NavLink to={`/t/${tenantId}/${item.to}`} className={activeNav(className)} end={false}>
              {children}
            </NavLink>
          )}
        />
      ))}
    </Sidebar>
  );
}

/** NavItem styles assume inactive; NavLink sets aria-current="page" when active, which these variants style. */
function activeNav(className: string): string {
  return `${className} aria-[current=page]:bg-brand-tint aria-[current=page]:text-brand-hover aria-[current=page]:[&_svg]:text-brand-primary`;
}

function TenantTopbar() {
  const { tenantId, membership, me, settings, timeZone } = useTenant();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const live = useLive();
  const [signingOut, setSigningOut] = React.useState(false);
  const others = me.memberships.filter((m) => m.tenantId !== tenantId);

  const doSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      qc.clear();
      navigate('/iniciar-sesion', { replace: true });
    }
  };

  return (
    <Topbar
      start={
        <div className="flex min-w-0 items-center gap-2">
          {others.length > 0 ? (
            <Menu>
              <MenuTrigger asChild>
                <Button variant="ghost" className="min-w-0 max-w-[min(360px,60vw)] px-2" aria-label={`Negocio: ${settings.name}. Cambiar de negocio`}>
                  <Building2 aria-hidden />
                  <span className="truncate font-semibold text-text-primary">{settings.name}</span>
                  <ChevronDown aria-hidden />
                </Button>
              </MenuTrigger>
              <MenuContent align="start">
                <MenuLabel>Cambiar de negocio</MenuLabel>
                {others.map((m) => (
                  <MenuItem key={m.tenantId} onSelect={() => navigate(`/t/${m.tenantId}/resumen`)}>
                    <span className="truncate">{m.tenantName}</span>
                    {m.isDemo ? <Badge tone="warning">Demo</Badge> : null}
                  </MenuItem>
                ))}
              </MenuContent>
            </Menu>
          ) : (
            <p className="flex min-w-0 items-center gap-2 px-2 text-body font-semibold text-text-primary">
              <Building2 aria-hidden className="size-[18px] shrink-0 text-text-secondary" />
              <span className="truncate">{settings.name}</span>
            </p>
          )}
          {settings.isDemo || membership.isDemo ? (
            <Badge tone="warning" title="Datos sintéticos de demostración">
              Demo
            </Badge>
          ) : null}
          <span className="hidden items-center gap-1 text-helper text-text-secondary xl:inline-flex">
            <Globe2 aria-hidden className="size-4" />
            {timeZoneLabel(timeZone)}
          </span>
        </div>
      }
      end={
        <>
          <span
            className="hidden items-center gap-1.5 text-helper text-text-secondary md:inline-flex"
            title={live.status === 'open' ? 'Actualizaciones en tiempo real activas' : live.status === 'polling' ? 'Actualizando periódicamente' : 'Conectando…'}
          >
            <Radio aria-hidden className={live.status === 'open' ? 'size-4 text-success' : 'size-4 text-warning'} />
            {live.status === 'open' ? 'En vivo' : live.status === 'polling' ? 'Actualización periódica' : 'Conectando…'}
          </span>
          <Menu>
            <MenuTrigger asChild>
              <Button variant="ghost" className="px-1.5" aria-label={`Cuenta de ${me.user.name}`}>
                <Avatar name={me.user.name} size="sm" />
                <ChevronDown aria-hidden className="hidden sm:block" />
              </Button>
            </MenuTrigger>
            <MenuContent>
              <MenuLabel>
                <span className="block truncate font-medium text-text-primary">{me.user.name}</span>
                <span className="block truncate font-normal">{me.user.email}</span>
              </MenuLabel>
              <MenuSeparator />
              {me.memberships.length > 1 ? (
                <MenuItem icon={<Building2 aria-hidden />} onSelect={() => navigate('/negocios')}>
                  Mis negocios
                </MenuItem>
              ) : null}
              {me.user.isPlatformAdmin ? (
                <MenuItem icon={<Bot aria-hidden />} onSelect={() => navigate('/admin/uso-ia')}>
                  Uso de IA (interno)
                </MenuItem>
              ) : null}
              <MenuItem icon={<LogOut aria-hidden />} disabled={signingOut} onSelect={() => void doSignOut()}>
                Cerrar sesión
              </MenuItem>
            </MenuContent>
          </Menu>
        </>
      }
    />
  );
}

function ShellSkeleton() {
  return (
    <div className="min-h-dvh bg-surface-canvas" aria-busy="true">
      <div className="fixed inset-y-0 left-0 hidden w-sidebar border-r border-border-subtle bg-surface-default p-5 lg:block">
        <Skeleton className="h-7 w-36" label="Cargando" />
      </div>
      <div className="lg:pl-sidebar">
        <div className="h-topbar border-b border-border-subtle bg-surface-default" />
        <div className="grid gap-4 p-7">
          <Skeleton className="h-8 w-48" />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-36 rounded-card" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
